import { Component, type ReactNode } from "react";
import { logError, scrubMessage, type TelemetryProperties } from "../../telemetry";
import { Notice } from "./Notice";
import { WarningIcon } from "./icons";
import styles from "./TelemetryErrorBoundary.module.css";

/**
 * Reusable React error boundary that standardises how render-time exceptions
 * are handled across the add-in. It catches any error thrown below it, ships a
 * single standardised `error.react.boundary` telemetry event (reusing the
 * shared add-in pipeline), and renders a recoverable fallback instead of a
 * silently-blank surface.
 *
 * Scope it as widely or narrowly as you like:
 *   - App root (no `boundary`) — last-resort catch for the whole tree.
 *   - Per feature/module (`boundary="deckorate"`) — isolates a crash to that
 *     subtree and attributes it, so a broken Deckorate view doesn't blank the
 *     whole task pane and AiVA Studio can see WHICH area failed.
 *
 * Attribution: `boundary` is stamped as the event's `panelName`, and any extra
 * allow-listed `properties` (e.g. `{ connectorSystemName: "deckorate" }`) are
 * merged on. Only the error type and a PII-scrubbed message leave the browser.
 */
export interface TelemetryErrorBoundaryProps {
  readonly children: ReactNode;
  /**
   * Stable identifier for this boundary's scope, attached to the emitted event
   * as `panelName` (e.g. "deckorate"). Omit for an app-level boundary that
   * emits no attribution.
   */
  readonly boundary?: string;
  /**
   * Extra allow-listed telemetry properties merged onto the event — e.g.
   * `{ connectorSystemName: "deckorate" }` to tag the owning integration.
   */
  readonly properties?: TelemetryProperties;
  /**
   * Fallback UI after a caught error. The function form receives a `reset`
   * callback that clears the error and re-renders `children` (retry). Defaults
   * to a minimal recoverable message (overridable via `label`).
   */
  readonly fallback?: ReactNode | ((reset: () => void) => ReactNode);
  /** Heading for the default fallback. Defaults to "Something went wrong". */
  readonly title?: string;
  /** Message for the default fallback. Ignored when `fallback` is provided. */
  readonly label?: string;
  /**
   * Text for the default fallback's recovery button. Defaults to "Retry".
   * Ignored when a custom `fallback` is provided.
   */
  readonly actionLabel?: string;
  /**
   * Handler for the default fallback's recovery button. Defaults to an in-place
   * reset — clear the error and re-render `children`. App-level callers pass a
   * hard reload instead, since re-rendering the same broken tree can't recover.
   * Ignored when a custom `fallback` is provided.
   */
  readonly onAction?: () => void;
}

interface TelemetryErrorBoundaryState {
  readonly errored: boolean;
}

const DEFAULT_TITLE = "Something went wrong";
const DEFAULT_LABEL = "AiVA hit an unexpected error. Please close and re-open the task pane.";

export class TelemetryErrorBoundary extends Component<
  TelemetryErrorBoundaryProps,
  TelemetryErrorBoundaryState
> {
  state: TelemetryErrorBoundaryState = { errored: false };

  static getDerivedStateFromError(): TelemetryErrorBoundaryState {
    return { errored: true };
  }

  componentDidCatch(error: Error): void {
    const { boundary, properties } = this.props;
    // Merge scope (panelName) + caller-supplied allow-listed tags. Emit no
    // property bag at all for an unattributed app-level boundary so behaviour
    // is identical to the previous inline root boundary.
    const attribution: TelemetryProperties = {
      ...(boundary ? { panelName: boundary } : {}),
      ...properties,
    };
    const errorBlock = {
      type: error.name || "ReactBoundaryError",
      scrubbedMessage: scrubMessage(error.message),
    };
    // Attributed boundary → include the property bag; unattributed app-level
    // boundary → omit the arg entirely (2-arg call), identical to the previous
    // inline root boundary.
    if (Object.keys(attribution).length > 0) {
      logError("error.react.boundary", errorBlock, attribution);
    } else {
      logError("error.react.boundary", errorBlock);
    }
  }

  private readonly reset = (): void => this.setState({ errored: false });

  render(): ReactNode {
    if (!this.state.errored) return this.props.children;

    const { fallback, title, label, actionLabel, onAction } = this.props;
    if (typeof fallback === "function") return fallback(this.reset);
    if (fallback !== undefined) return fallback;

    return (
      <Notice
        icon={<WarningIcon />}
        title={title ?? DEFAULT_TITLE}
        message={label ?? DEFAULT_LABEL}
        action={
          <button type="button" className={styles.action} onClick={onAction ?? this.reset}>
            {actionLabel ?? "Retry"}
          </button>
        }
      />
    );
  }
}
